import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import { setPostIntegrationReturnPath } from '../../utils/integrations';

const PROVIDER_CONFIG = {
  'github-enterprise': {
    title: 'GitHub Enterprise',
    providerKey: 'github-enterprise',
    applicationLabel: 'Client ID',
    secretLabel: 'Client Secret',
    placeholder: 'https://github.company.com',
    docsUrl:
      'https://docs.gitdotme.com/docs/Integrations/github-enterprise-integration/'
  },
  gitlab: {
    title: 'Self-Hosted GitLab',
    providerKey: 'gitlab',
    applicationLabel: 'Application ID',
    secretLabel: 'Secret',
    placeholder: 'https://gitlab.company.com',
    docsUrl:
      'https://docs.gitdotme.com/docs/Integrations/self-hosted-gitlab-integration/'
  },
  bitbucket: {
    title: 'Self-Hosted Bitbucket',
    providerKey: 'bitbucket',
    applicationLabel: 'Client ID',
    secretLabel: 'Client Secret',
    placeholder: 'https://bitbucket.company.com',
    docsUrl:
      'https://docs.gitdotme.com/docs/Integrations/self-hosted-bitbucket-integration/'
  }
};

const CONNECTING_STAGES = [
  { progress: 18, label: 'Verifying access' },
  { progress: 44, label: 'Securing transit' },
  { progress: 72, label: 'Preparing redirect' },
  { progress: 100, label: 'Ready to connect' }
];

const SelfHostedIntegrationConnect = () => {
  const { provider } = useParams();
  const config = useMemo(() => PROVIDER_CONFIG[provider], [provider]);
  const [instanceUrl, setInstanceUrl] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [secret, setSecret] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [authorizeUrl, setAuthorizeUrl] = useState('');
  const [connectionProgress, setConnectionProgress] = useState(
    CONNECTING_STAGES[0].progress
  );
  const [connectionStatus, setConnectionStatus] = useState(
    CONNECTING_STAGES[0].label
  );
  const connectionTimersRef = useRef([]);

  const clearConnectionTimers = () => {
    connectionTimersRef.current.forEach(timerId =>
      window.clearTimeout(timerId)
    );
    connectionTimersRef.current = [];
  };

  const validateForm = () => {
    if (!instanceUrl.trim()) {
      toast.error('Instance URL is required.');
      return false;
    }

    try {
      const parsedUrl = new URL(instanceUrl.trim());
      if (parsedUrl.protocol !== 'https:') {
        toast.error('Instance URL must use HTTPS.');
        return false;
      }
    } catch (error) {
      toast.error('Enter a valid instance URL.');
      return false;
    }

    if (!applicationId.trim()) {
      toast.error(`${config.applicationLabel} is required.`);
      return false;
    }

    if (!secret.trim()) {
      toast.error(`${config.secretLabel} is required.`);
      return false;
    }

    return true;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!config || !validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setIsConnecting(true);
    setConnectionProgress(CONNECTING_STAGES[0].progress);
    setConnectionStatus(CONNECTING_STAGES[0].label);
    try {
      const response = await sendRequest(
        `/self-hosted-integrations/${config.providerKey}/start`,
        'POST',
        {
          instanceUrl: instanceUrl.trim(),
          applicationId: applicationId.trim(),
          secret
        }
      );

      if (!response?.authorizeUrl) {
        throw new Error('Authorization URL was not returned.');
      }

      setPostIntegrationReturnPath();
      setAuthorizeUrl(response.authorizeUrl);
    } catch (error) {
      clearConnectionTimers();
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Could not start the self-hosted integration.'
      );
      setIsSubmitting(false);
      setIsConnecting(false);
      setAuthorizeUrl('');
    }
  };

  useEffect(() => {
    if (!isConnecting || !authorizeUrl) {
      return undefined;
    }

    clearConnectionTimers();

    connectionTimersRef.current = [
      window.setTimeout(() => {
        setConnectionProgress(CONNECTING_STAGES[1].progress);
        setConnectionStatus(CONNECTING_STAGES[1].label);
      }, 1200),
      window.setTimeout(() => {
        setConnectionProgress(CONNECTING_STAGES[2].progress);
        setConnectionStatus(CONNECTING_STAGES[2].label);
      }, 3000),
      window.setTimeout(() => {
        setConnectionProgress(CONNECTING_STAGES[3].progress);
        setConnectionStatus(CONNECTING_STAGES[3].label);
      }, 5000),
      window.setTimeout(() => {
        window.location.href = authorizeUrl;
      }, 6000)
    ];

    return () => {
      clearConnectionTimers();
    };
  }, [authorizeUrl, isConnecting]);

  if (!config) {
    return (
      <div className="mx-auto max-w-3xl rounded-2xl border border-stroke bg-white p-8 shadow-sm dark:border-strokedark dark:bg-boxdark">
        <h2 className="text-2xl font-bold text-black dark:text-white">
          Unknown self-hosted provider
        </h2>
        <p className="mt-2 text-sm text-body-color dark:text-body-color-dark">
          Choose a supported self-hosted integration from the integrations page.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      {isConnecting && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-md" />
          <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-[28px] border border-sky-100/80 bg-white shadow-[0_32px_80px_rgba(15,23,42,0.28)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.18),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(34,197,94,0.14),transparent_30%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]" />
            <div className="relative px-6 py-8 sm:px-8 sm:py-10">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                Secure Handshake
              </div>

              <div className="mx-auto mb-6 flex h-28 w-28 items-center justify-center rounded-[30px] border border-sky-100 bg-white shadow-[0_20px_50px_rgba(56,189,248,0.18)]">
                <div className="flex h-16 w-16 animate-[spin_8s_linear_infinite] items-center justify-center rounded-full border-4 border-dashed border-sky-300">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 shadow-sm">
                    <span className="text-2xl font-black text-sky-700">G</span>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <h3 className="text-3xl font-black tracking-tight text-slate-950">
                  Securing connection
                </h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-600">
                  We&apos;re preparing a secure redirect to {config.title}.
                </p>
              </div>

              <div className="mt-8 rounded-2xl border border-slate-200/80 bg-slate-50/90 p-4">
                <div className="h-3 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-500 transition-all duration-700 ease-out"
                    style={{ width: `${connectionProgress}%` }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 text-sm text-slate-600">
                  <span>{connectionStatus}</span>
                  <span className="font-semibold text-slate-900">
                    {connectionProgress}%
                  </span>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3 text-center text-xs text-slate-500">
                <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-3">
                  Limited access
                </div>
                <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-3">
                  No secret capture
                </div>
                <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-3">
                  Redirecting shortly
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-stroke bg-white p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
        <h2 className="text-2xl font-bold text-black dark:text-white">
          Connect {config.title}
        </h2>
        <p className="mt-2 text-sm leading-6 text-body-color dark:text-body-color-dark">
          Enter the OAuth application credentials from your self-hosted git
          server. Gitme will redirect you to authorize repository access.
        </p>
        <a
          href={config.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-100 dark:hover:border-slate-500 dark:hover:bg-slate-700/80"
        >
          View Setup Guide
        </a>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-stroke bg-white p-6 shadow-sm dark:border-strokedark dark:bg-boxdark"
      >
        <div className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-semibold text-black dark:text-white">
              Instance URL
            </label>
            <input
              type="url"
              value={instanceUrl}
              onChange={event => setInstanceUrl(event.target.value)}
              placeholder={config.placeholder}
              className="w-full rounded-lg border border-stroke bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-primary dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-black dark:text-white">
              {config.applicationLabel}
            </label>
            <input
              type="text"
              value={applicationId}
              onChange={event => setApplicationId(event.target.value)}
              placeholder={`Enter your ${config.applicationLabel}`}
              className="w-full rounded-lg border border-stroke bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-primary dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-black dark:text-white">
              {config.secretLabel}
            </label>
            <input
              type="password"
              value={secret}
              onChange={event => setSecret(event.target.value)}
              placeholder={`Enter your ${config.secretLabel}`}
              className="w-full rounded-lg border border-stroke bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-primary dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
          </div>
        </div>

        <div className="mt-7 flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? 'Starting connection...' : 'Connect'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SelfHostedIntegrationConnect;
